import { isEqual, isPlainObject } from 'lodash';

/** Identity of the server copy a draft was seeded from: a new id or a new version is a different record. */
export const seedKey = (id: string, version?: string | number | null): string =>
  `${id}@${version ?? ''}`;

/** Reseed on a new record or version; the same key (a plain refetch) keeps the operator's unsaved edits. */
export const shouldReseed = (seeded: string | null, next: string): boolean => seeded !== next;

export const isDirty = <T>(draft: T, saved: T): boolean => !isEqual(draft, saved);

/** What `useRecordDraft` holds. The id sits beside the key so "same record, new version" needs no key parsing. */
export interface DraftSeed<D> {
  id: string;
  key: string;
  draft: D;
  saved: D;
  /** A save resolved: the next version of this record keeps any edits the operator has made by then. */
  rebase: boolean;
}

/** The top-level fields of `draft` that differ from `sent`, or null when nothing changed. */
const changedFields = <D>(draft: D, sent: D): Partial<D> | null => {
  if (!isPlainObject(draft) || !isPlainObject(sent)) return isDirty(draft, sent) ? draft : null;
  const changed = Object.fromEntries(
    Object.entries(draft as Record<string, unknown>).filter(
      ([k, v]) => !isEqual(v, (sent as Record<string, unknown>)[k]),
    ),
  ) as Partial<D>;
  return Object.keys(changed).length > 0 ? changed : null;
};

// Only the in-flight fields survive; every other field comes from the fresh copy (a restore, a
// normalized value, or someone else's concurrent save).
const overlay = <D>(base: D, inflight: Partial<D>): D =>
  isPlainObject(base) ? { ...base, ...inflight } : (inflight as D);

/**
 * Seed from a loaded record. After a save that left edits pending on the same record id, the
 * operator's edits as they stand now (not when the save resolved) are laid over the new version.
 */
export const reseedDraft = <D>(
  seed: DraftSeed<D> | null,
  id: string,
  key: string,
  next: D,
): DraftSeed<D> => {
  const local = seed?.rebase && seed.id === id ? changedFields(seed.draft, seed.saved) : null;
  return { id, key, saved: next, draft: local ? overlay(next, local) : next, rebase: false };
};

/**
 * A save of `submitted` resolved. The baseline becomes the server copy (or what was sent); only the
 * fields edited since the request went out stay on the draft, dirty against that baseline.
 */
export const applySaved = <D>(seed: DraftSeed<D>, submitted: D, server?: D): DraftSeed<D> => {
  const saved = server ?? submitted;
  const inflight = changedFields(seed.draft, submitted);
  return {
    ...seed,
    saved,
    draft: inflight ? overlay(saved, inflight) : saved,
    // Always: the operator may type before the version-bump refetch lands; a clean draft reseeds fresh anyway.
    rebase: true,
  };
};

/** RTK Query keeps the previous argument's data while the next loads: never seed a record that is not the route's. */
export const canSeed = <R>(record: R, id: string, recordId?: (record: R) => string): boolean =>
  !recordId || recordId(record) === id;
